import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { KpiCard, KpiStrip } from '../../../cc'
import { ReportTable } from '../../../cc/ReportTable'
import { CC } from '../../../cc/tokens'
import { BarChart } from '../../charts/Charts'
import { HeatGrid, heatKey } from '../../charts/HeatGrid'
import { LineChart, type LineSeries } from '../../charts/LineChart'
import { reportInput, reportSegment, reportSegmentBtn } from '../../ui'
import { useReportTableState } from '../../useReportTableState'
import { toApiParams, type AlertFilters } from '../alertFilters'
import { DOW_LABELS, fmtDayBucket, fmtDec, fmtInt, kindLabel } from '../alertLabels'
import type { TrendDimension, TrendEnvelope, TrendGrain, TrendRow } from '../alertTypes'
import { useAlertAnalytics } from '../useAlertAnalytics'
import { AlertDrillList, type DrillScope } from './AlertDrillList'
import { asRows, columnDefs, makeExport, useViewExport, ViewCard, ViewState, type ViewProps } from './viewKit'

const COLUMNS = ['bucket', 'dim_value', 'alerts', 'critical'] as const
const TOP_N = 8

const GRAINS: { id: TrendGrain; label: string; hint: string }[] = [
  { id: 'day', label: 'Daily', hint: 'One point per IST day.' },
  { id: 'week', label: 'Weekly', hint: 'One point per ISO week, labelled by its Monday.' },
  { id: 'hour', label: 'Hour of day', hint: 'All days folded onto a 24-hour clock (IST).' },
  { id: 'dow_hour', label: 'Weekday × hour', hint: 'Every weekday-hour cell, summed over the window.' },
]

const DIMENSIONS: { id: TrendDimension; label: string }[] = [
  { id: 'kind', label: 'Alert type' },
  { id: 'severity', label: 'Severity' },
  { id: 'driver', label: 'Driver' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'location', label: 'Place' },
  { id: 'shift', label: 'Shift' },
]

/** Categorical palette for up to TOP_N series + 'other' (always the last, grey). Starts
 *  from the console's own accent set so a critical series still reads as rose. */
const SERIES_COLORS = ['#2563EB', CC.dangerBright, CC.warnBright, CC.okBright, CC.north, CC.south, '#D946EF', '#0D9488']
const OTHER_COLOR = '#CBD5E1'

const SEVERITY_COLORS: Record<string, string> = { CRITICAL: CC.dangerBright, WARN: CC.warnBright, INFO: CC.infoBright }

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))
const DOWS = ['0', '1', '2', '3', '4', '5', '6']

function dimLabel(dimension: TrendDimension, v: string): string {
  if (v === 'other') return 'Other'
  if (dimension === 'kind') return kindLabel(v)
  if (dimension === 'shift') return v === 'night' ? 'Night' : v === 'day' ? 'Day' : v
  return v
}

function bucketMs(bucket: string): number {
  return new Date(`${bucket}T00:00:00+05:30`).getTime()
}

/** Alerts over time, split by one dimension (top-N + other). Grain picks the chart: a
 *  line for calendar time, bars for the 24-hour clock, a heat grid for weekday × hour. */
export function TrendsView({ filters, setFilters }: ViewProps): JSX.Element {
  const params = useMemo(() => {
    const p = toApiParams(filters)
    p.set('grain', filters.grain)
    p.set('dimension', filters.dimension)
    p.set('top_n', String(TOP_N))
    return p
  }, [filters])
  const { rows, status, error } = useAlertAnalytics<TrendRow, TrendEnvelope>('/trend', params)
  const { grain, dimension } = filters

  const model = useMemo(() => {
    const totalsByDim = new Map<string, number>()
    const totalsByBucket = new Map<string, { alerts: number; critical: number }>()
    let alerts = 0
    let critical = 0
    for (const r of rows) {
      totalsByDim.set(r.dim_value, (totalsByDim.get(r.dim_value) ?? 0) + r.alerts)
      const b = totalsByBucket.get(r.bucket) ?? { alerts: 0, critical: 0 }
      b.alerts += r.alerts
      b.critical += r.critical
      totalsByBucket.set(r.bucket, b)
      alerts += r.alerts
      critical += r.critical
    }
    // Series order: biggest first, 'other' pinned last so it always takes the grey.
    const dims = [...totalsByDim.entries()]
      .filter(([d]) => d !== 'other')
      .sort((a, b) => b[1] - a[1])
      .map(([d]) => d)
    if (totalsByDim.has('other')) dims.push('other')
    const colorOf = (d: string, i: number) =>
      d === 'other' ? OTHER_COLOR : dimension === 'severity' && SEVERITY_COLORS[d] ? SEVERITY_COLORS[d]! : SERIES_COLORS[i % SERIES_COLORS.length]!
    const series = dims.map((d, i) => ({ key: d, label: dimLabel(dimension, d), color: colorOf(d, i) }))

    const buckets = [...totalsByBucket.keys()].sort()
    const peak = [...totalsByBucket.entries()].sort((a, b) => b[1].alerts - a[1].alerts)[0] ?? null
    return { series, buckets, totalsByBucket, alerts, critical, peak, perBucket: buckets.length ? alerts / buckets.length : 0 }
  }, [rows, dimension])

  const lineSeries: LineSeries[] = useMemo(() => {
    if (grain !== 'day' && grain !== 'week') return []
    return model.series.map((s) => {
      const byBucket = new Map<string, number>()
      for (const r of rows) if (r.dim_value === s.key) byBucket.set(r.bucket, (byBucket.get(r.bucket) ?? 0) + r.alerts)
      // Zero-fill the buckets this series is absent from, so the line drops to the
      // baseline on a quiet day instead of drawing a straight bridge across it.
      return { ...s, points: model.buckets.map((b) => ({ x: bucketMs(b), y: byBucket.get(b) ?? 0 })).filter((p) => Number.isFinite(p.x)) }
    })
  }, [rows, model, grain])

  const hourData = useMemo(() => {
    if (grain !== 'hour') return []
    return HOURS.map((h) => {
      const values: Record<string, number> = {}
      for (const r of rows) if (r.bucket === h) values[r.dim_value] = (values[r.dim_value] ?? 0) + r.alerts
      return { label: h, values, title: `${h}:00 IST` }
    })
  }, [rows, grain])

  const heat = useMemo(() => {
    const values = new Map<string, number>()
    if (grain !== 'dow_hour') return values
    for (const r of rows) {
      const [dow, hh] = r.bucket.split('-')
      if (dow === undefined || hh === undefined) continue
      const k = heatKey(dow, hh)
      values.set(k, (values.get(k) ?? 0) + r.alerts)
    }
    return values
  }, [rows, grain])

  const table = useReportTableState('alerts-trend', asRows(rows))
  const defs = useMemo(() => columnDefs(COLUMNS, {
    bucket: {
      label: grain === 'hour' ? 'Hour (IST)' : grain === 'dow_hour' ? 'Weekday-hour' : grain === 'week' ? 'Week of' : 'Day',
      render: (v) => <span className="font-mono text-[12px] tabular-nums">{formatBucket(grain, String(v))}</span>,
    },
    dim_value: {
      label: DIMENSIONS.find((d) => d.id === dimension)?.label ?? 'Series',
      render: (v) => {
        const s = model.series.find((x) => x.key === v)
        return (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-700">
            <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: s?.color ?? OTHER_COLOR }} />
            {dimLabel(dimension, String(v))}
          </span>
        )
      },
    },
  }), [grain, dimension, model.series])
  const exportFn = useMemo(
    () => makeExport(rows, [...COLUMNS], `alerts-trend-${grain}-${dimension}`, `Alert trend by ${dimension} (${grain})`, filters),
    [rows, filters, grain, dimension],
  )
  useViewExport(exportFn)

  const grainMeta = GRAINS.find((g) => g.id === grain)!
  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <div className={reportSegment} role="group" aria-label="Time grain">
        {GRAINS.map((g) => (
          <button
            key={g.id}
            type="button"
            aria-pressed={grain === g.id}
            onClick={() => setFilters({ grain: g.id })}
            className={clsx(reportSegmentBtn(grain === g.id), '!px-2.5 !py-1 !text-[11.5px]')}
          >
            {g.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
        split by
        <select
          value={dimension}
          onChange={(e) => setFilters({ dimension: e.target.value as TrendDimension })}
          className={clsx(reportInput, '!py-1')}
          aria-label="Split dimension"
        >
          {DIMENSIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </label>
    </div>
  )

  // A bucket row clicked in the table narrows the alert list to that bucket (day/week:
  // its own window; hour grains have no clock filter on /list, so only the split value).
  const [pick, setPick] = useState<{ bucket: string; dim: string } | null>(null)
  const scope = useMemo(() => (pick ? trendScope(pick, grain, dimension, filters, () => setPick(null)) : null), [pick, grain, dimension, filters])

  const chartLabel = `Alerts by ${grainMeta.label.toLowerCase()}, split by ${DIMENSIONS.find((d) => d.id === dimension)?.label.toLowerCase()}`

  return (
    <div className="space-y-4">
      {/* KPIs first, like every other tab -- the strip reads the same rows as the chart. */}
      {status === 'ok' && rows.length > 0 ? (
        <KpiStrip>
          <KpiCard label="Alerts" value={model.alerts} />
          <KpiCard label="Critical" value={model.critical} tone={model.critical > 0 ? 'danger' : 'neutral'} dot={model.critical > 0} />
          <KpiCard
            label={`Busiest ${grain === 'dow_hour' ? 'cell' : grain === 'hour' ? 'hour' : grain}`}
            value={model.peak ? formatBucket(grain, model.peak[0]) : '—'}
            sub={model.peak ? `${fmtInt(model.peak[1].alerts)} alerts, ${fmtInt(model.peak[1].critical)} critical` : undefined}
          />
          <KpiCard label={`Per ${grain === 'dow_hour' ? 'cell' : grain === 'hour' ? 'hour' : grain}`} value={fmtDec(model.perBucket)} sub={`${fmtInt(model.buckets.length)} buckets with alerts`} />
        </KpiStrip>
      ) : null}

      <ViewCard title="Trend" sub={grainMeta.hint} actions={controls}>
        <ViewState
          status={status} error={error} rowCount={rows.length} filters={filters} setFilters={setFilters}
          noData={{ title: 'No alerts in this window', body: 'Nothing fired for this site between these dates, so there is no trend to draw.' }}
        >
          {grain === 'day' || grain === 'week' ? (
            <LineChart
              series={lineSeries}
              height={200}
              yLabel="alerts"
              formatX={(x) => fmtDayBucket(new Date(x).toISOString().slice(0, 10))}
              ariaLabel={chartLabel}
            />
          ) : grain === 'hour' ? (
            <div role="img" aria-label={chartLabel}>
              <BarChart height={180} data={hourData} series={model.series} />
            </div>
          ) : (
            <HeatGrid
              rows={DOWS}
              cols={HOURS}
              values={heat}
              color={CC.dangerBright}
              rowLabel={(d) => DOW_LABELS[Number(d)] ?? d}
              colLabel={(h) => (Number(h) % 3 === 0 ? h : '')}
              ariaLabel={`Alerts by weekday and hour of day (IST)`}
            />
          )}
        </ViewState>
      </ViewCard>

      {status === 'ok' && rows.length > 0 ? (
        <>
          <ViewCard title="Every bucket" sub={`Top ${TOP_N} ${DIMENSIONS.find((d) => d.id === dimension)?.label.toLowerCase()} values, everything else folded into Other.`} bodyClassName="p-0">
            <ReportTable
              columns={[...COLUMNS]}
              rows={table.sorted}
              columnDefs={defs}
              sort={table.sort}
              onSort={table.toggleSort}
              onRowClick={(row) => {
                const next = { bucket: String(row.bucket), dim: String(row.dim_value) }
                setPick((cur) => (cur && cur.bucket === next.bucket && cur.dim === next.dim ? null : next))
              }}
              compact
              applyLoadStateLabels={false}
              className="rounded-none ring-0 shadow-none"
            />
          </ViewCard>
        </>
      ) : null}

      <AlertDrillList
        filters={filters}
        setFilters={setFilters}
        scope={scope}
        idleHint="Click a bucket row above to see just those alerts."
      />
    </div>
  )
}

const DIM_PARAM: Record<TrendDimension, string> = {
  kind: 'kind', severity: 'severity', driver: 'driver', vehicle: 'vehicle_id', location: 'hub', shift: 'shift',
}
const DAY_MS = 24 * 60 * 60 * 1000

/** Turn a clicked trend bucket into /list params. 'other' is a fold, not a value, so it
 *  adds no dimension filter; a day/week bucket becomes its own IST window, clipped to the
 *  report's. */
export function trendScope(
  pick: { bucket: string; dim: string },
  grain: TrendGrain,
  dimension: TrendDimension,
  filters: Pick<AlertFilters, 'from' | 'to'>,
  onClear: () => void,
): DrillScope {
  const params: Record<string, string> = {}
  const parts: string[] = [formatBucket(grain, pick.bucket)]
  if (pick.dim && pick.dim !== 'other' && !(dimension === 'vehicle' && pick.dim === 'no vehicle')) {
    params[DIM_PARAM[dimension]] = pick.dim
    parts.push(dimension === 'kind' ? kindLabel(pick.dim) : pick.dim)
  }
  if (grain === 'day' || grain === 'week') {
    const start = Date.parse(`${pick.bucket}T00:00:00+05:30`)
    if (Number.isFinite(start)) {
      const from = Math.max(start, filters.from)
      const to = Math.min(start + (grain === 'week' ? 7 : 1) * DAY_MS, filters.to)
      if (to > from) {
        params.from = new Date(from).toISOString()
        params.to = new Date(to).toISOString()
      }
    }
  }
  return { label: parts.join(' · '), params, onClear }
}

function formatBucket(grain: TrendGrain, bucket: string): string {
  switch (grain) {
    case 'day': return fmtDayBucket(bucket)
    case 'week': return `wk of ${fmtDayBucket(bucket)}`
    case 'hour': return `${bucket}:00`
    case 'dow_hour': {
      const [dow, hh] = bucket.split('-')
      return `${DOW_LABELS[Number(dow)] ?? dow} ${hh}:00`
    }
  }
}
